/*
 * User-facing text for the scholarships API's problem codes (scholarships/common in the backend).
 * The API's `detail` is never shown: the wording lives here so it can be translated.
 * Codes that only a frontend bug can cause get a generic message and are logged by the caller.
 */
const MESSAGES = {
  // Anywhere
  "invalid-request": "Some fields are invalid. Check the highlighted fields.",
  "not-found": "Not found, or you do not have access.",
  "stale-version": "Someone else changed this. We reloaded it; please check and try again.",
  "request-in-progress": "Still processing your previous click. Try again in a moment.",

  // Scholarship set-up
  "not-draft": "This can no longer be changed.",
  "field-locked": "This field can no longer change.",
  "marking-not-open": "Marking starts once applications close.",
  "owner-only": "Only the scholarship owner can ask the student for more.",
  "answer-empty": "Add a file or a note before sending.",
  "request-answered": "This request has already been answered.",
  "too-many-files": "A request takes up to 10 files.",
  "not-reopenable": "This application can no longer be changed: the deadline has passed or its review has started.",
  "set-up-locked": "Students have started applying, so the set-up can no longer change.",
  missing: "This is required to publish.",
  "publish-incomplete": "The scholarship is not ready to publish yet.",
  "invalid-transition": "Not possible in the scholarship's current state.",
  "type-locked": "The type is set when the call is created and cannot change.",
  "rwanda-only": "Private institution loans are only for studies in Rwanda.",
  "no-travel": "This loan is for studies in Rwanda, so there is no travel or ticket.",
  "no-institution": "Your account is not linked to an institution. Ask HEC to link it.",
  "invalid-sheet": "Some rows of the sheet are invalid. Nothing was saved.",
  "invalid-decision": "Write Granted or Not granted.",
  "ambiguous-registration-number": "Several students you uploaded have this registration number.",
  "duplicate-registration-number": "This registration number is on another row too.",
  "registration-number-of-another-institution": "This registration number belongs to a student of another institution.",
  "uploaded-for-another-year": "This student was uploaded for another academic year.",
  "unknown-college": "No college has this code or name.",
  "college-of-another-institution": "This college is not one of your institution's.",
  "unknown-qualification": "No qualification has this code.",
  "unknown-combination": "No combination has this code.",
  "invalid-gender": "Write Male or Female.",
  "student-has-another-loan": "This student's registration or index number is already on another loan.",
  "invalid-academic-year": "Choose an academic year such as 2026-2027.",
  "weights-must-total-100": "Criterion weights must add up to 100.",
  "rubric-locked": "Marking has started; the criteria can no longer change.",
  "programme-exists": "This programme is already in the catalogue.",
  "programme-in-use": "A published scholarship offers this programme, so it can no longer change.",

  // Publish checklist (errors of publish-incomplete)
  "sponsor-missing": "Choose a partner.",
  "approval-evidence-missing": "Upload the approval evidence: the DG's signed letter or MINAFET's call letter.",
  "privacy-evidence-missing": "Upload the reason for private visibility (under Visibility) first.",

  // Invitations to a private scholarship
  "not-private": "Only a private scholarship takes invitations.",
  "invitations-closed": "Students can be invited only while the scholarship is a draft or open.",
  "invalid-email": "This is not a valid email address.",
  "sponsor-not-allowed": "A government loan has no partner.",
  "dates-invalid": "Set an opening date before the closing date.",
  "no-programmes": "Add at least one programme.",
  "no-requirements": "Add at least one required document.",
  "rubric-missing": "Set up the selection criteria.",

  // People
  "account-exists": "An account with this email already exists.",
  "not-a-scholarship-owner": "This person does not have the scholarship owner role.",
  "not-an-evaluator": "This person does not have the evaluator role.",
  "already-member": "Already added.",
  "already-assigned": "Already added.",
  "admin-override-required": "This person is also an HEC Admin. Confirm to assign them anyway.",
  "unknown-person": "Nobody with this account ID has signed in to HECMIS yet. If the account exists in Keycloak, ask them to sign in once, then add them.",

  // Students and applications
  "already-applied": "You already have an application for this scholarship.",
  "scholarship-closed": "Applications for this scholarship are closed.",
  "profile-not-found": "Fill in your academic profile to see which scholarships you can apply for.",
  "profile-required": "Fill in your academic profile before applying.",
  "invalid-profile": "The academic profile is not valid.",
  "programme-not-offered": "Choose one of this scholarship's programmes.",
  "unsupported-type": "Upload a PDF, JPEG or PNG file.",
  "file-too-large": "The file is larger than the limit for this document.",
  "malware-detected": "This file was rejected by the virus check.",
  "document-not-available": "This file is still being checked. Try again shortly.",
  "submission-invalid": "The application cannot be submitted yet.",
  "withdrawal-closed": "A decision has been made; the application can no longer be withdrawn.",
  "deadline-passed": "The deadline has passed.",
  "missing-document": "This document is required.",
  "document-not-clean": "This document did not pass the virus check. Upload it again.",
  "missing-field": "This field is required.",
  // A student's consent statement at submit, and an evaluator's consent to the committee's rules
  "consent-required": "Give your consent first.",
  "invalid-currency": "Choose a currency.",
  "invalid-amount": "Enter a whole amount of 0 or more.",
  "invalid-date": "Enter a valid date.",
  "end-before-start": "The end date must be after the start date.",
  "unknown-country": "Choose a country from the list.",
  "not-abroad": "An abroad loan is for studies outside Rwanda. For studies in Rwanda, apply for a local loan.",

  // Review and decision
  "approved-funding-required": "Set the approved loan amounts (currency and tuition) for every awarded student.",
  "loan-not-approved": "The loan has not been approved yet.",
  "already-responded": "You have already answered.",
  "loan-not-accepted": "Accept the loan first.",
  "ticket-issued": "BRD has already bought your ticket, so the travel details can no longer change.",
  "travel-incomplete": "Fill in every travel detail first.",
  "invalid-passport-number": "Enter the passport number as printed: 6 to 12 letters and digits.",
  "date-in-past": "Choose a date in the future.",
  "passport-expires-before-travel": "Your passport must be valid after the travel date.",
  "travel-not-sent": "The student has not sent their travel details yet.",
  "visa-missing": "The student needs a visa for their country of study and has not uploaded it yet. Buy the ticket once they do.",
  "no-approved-amounts": "This loan has no approved amounts, so no payment can be recorded.",
  "invalid-kind": "Choose what was paid.",
  "date-in-future": "The date cannot be in the future.",
  "already-marked": "You have already submitted marks for this applicant.",
  "already-removed": "You have already removed this applicant.",
  "evaluation-submitted": "Already submitted.",
  "incomplete-marks": "Mark every criterion.",
  "not-in-review": "This application is no longer being marked.",
  "scholarship-not-closed": "Close the scholarship first.",
  "award-list-exists": "The award list already exists.",
  "award-list-finalised": "The award list was already finalised.",
  "entries-incomplete": "Every application needs an outcome.",
  "applications-not-reviewed": "Some applications are still being marked.",
  "separation-of-duties": "You marked this scholarship's applications, so someone else must send the list.",

  // Approvals for every scholarship; amounts only for government loans
  // Settings: what the forms offer
  "academic-year-past": "This academic year has ended. Choose the current academic year or the next one.",
  "academic-year-too-far": "This academic year is too far ahead. Choose the current academic year or the next one.",
  "destination-not-allowed": "An abroad loan has no destination: each student gives the country they were admitted in.",
  "programme-needs-place": "This programme has no university or country. Add them before offering it here.",
  "country-not-offered": "HEC no longer offers this destination country. Choose another one, or ask the Scholarship Admin to add it in Settings.",
  "currency-not-offered": "HEC does not offer this currency. Choose another one.",
  "invalid-academic-year-range": "Only the current and next academic years can be added.",
  "at-least-one": "Keep at least one value in the list.",
  "name-exists": "This name is already in the list.",
  "level-retired": "This education level is retired. Choose another one.",
  "field-retired": "This field of study is retired. Choose another one.",
  "already-retired": "This item is already retired.",
  "not-retired": "This item is not retired.",

  // The DG signs off (or returns) a whole award list, for scholarships other than government loans
  "signed-off-as-a-list": "The DG signs off this scholarship's award list as a whole, not award by award.",
  "invalid-role": "Choose one of the staff roles or Student.",
  "institution-required": "Choose the institution this HLI admin works for.",
  // A student account needs what self-registration asks for
  "student-contact-invalid": "A student account needs a 16-digit national ID and a Rwandan mobile number.",
  "invalid-national-id": "Enter the 16-digit national ID number.",
  "invalid-phone-number": "Enter a Rwandan mobile number, like 0781234567 or +250781234567.",
  "national-id-exists": "A student with this national ID already has an account.",
  "unknown-institution": "This institution does not exist.",
  "not-with-sponsor": "This nominee is not waiting for the partner's answer any more. Refresh the list.",
  "letter-required": "Attach the partner's letter when recording the answer for the partner.",
  "nominations-not-open": "Reserves can be sent once the nomination list is sent to the partner, and until nominations are closed.",
  "not-a-reserve": "Only a reserve can be sent to the partner.",
  "sponsor-answer-pending": "Some nominees are still waiting for the partner's answer.",
  "award-list-not-sent": "The award list has not been sent to the DG.",
  "award-list-signed-off": "The DG has already signed off this award list.",
  "decided-loan-by-loan": "A government loan's award list is not signed off: its loans go straight to the Minister.",
  "not-a-loan": "Only government loan scholarships carry loan amounts.",

  // The owner's follow-up of approved awardees
  "invalid-status-change": "Some of the selected awardees cannot take this step. Reload and check the selection.",
  "not-a-beneficiary": "This applicant's award is not approved, so there is nothing to follow up.",
  "status-unchanged": "This awardee is already at this step.",
  "withdrawn": "This applicant withdrew their application.",

  // Set-up (draft 3); the 422 codes come with one `errors` entry per problem
  "reason-required": "Say why you are making this change.",
  // Reopening a closed scholarship (draft 20)
  "reason-length": "Give a reason of 10 to 1000 characters.",
  "closing-date-past": "Choose a closing date in the future.",
  "results-already-told": "Some students have already been told their result, so this scholarship cannot be reopened.",
  "invalid-scholarship": "Some details are not valid. Check the highlighted fields.",
  "unknown-sponsor": "This partner does not exist.",
  "unknown-academic-year": "This academic year does not exist.",
  "invalid-quota": "Enter a whole number of 1 or more.",
  "unknown-programme": "One of these programmes does not exist.",
  "duplicate-programme": "A programme is listed twice.",
  "invalid-eligibility": "Some eligibility rules are not valid.",
  "unknown-level": "This education level does not exist.",
  "unknown-field": "This field of study does not exist.",
  "unknown-field-of-study": "This field of study does not exist.",
  "invalid-grade": "Enter a percentage from 0 to 100.",
  "invalid-year": "Enter a year from 1950 to 2100.",
  "invalid-percent": "Enter a percentage from 0 to 100.",
  "invalid-years": "Enter a whole number of years from 0 to 50.",
  "duplicate-code": "The same document is listed twice.",
  "duplicate-rule": "The same rule is listed twice.",
  "invalid-rubric": "The selection criteria are not valid.",
  "duplicate-criterion": "Two criteria have the same name.",
  "band-invalid": "A band's minimum is above its maximum.",

  // Sponsors and lifecycle (draft 4)
  "read-only": "This scholarship is cancelled or decided and can no longer change.",
  "sponsor-exists": "A partner with this name already exists.",
  "account-creation-unavailable": "New accounts cannot be created here yet. Create the account first, then add it.",

  // Publishing, review and the student's side
  "has-applications": "Not possible once students have applied.",
  "reopen-confirmation-required": "Reviewed applications would go back into review. Confirm to continue.",
  "precondition-required": "Something went wrong on our side. Please reload the page and try again.",
  "not-reviewed": "Not fully marked yet.",
  "evaluation-not-submitted": "This evaluation is not submitted, so there is nothing to reopen.",
  "conflict-declared": "You declared a conflict of interest on this application.",
};

// Field errors of `invalid-request` carry the backend's Bean Validation codes
const FIELD_RULES = {
  required: "This field is required.",
  "too-long": "This is too long.",
  NotBlank: "This field is required.",
  NotNull: "This field is required.",
  NotEmpty: "This field is required.",
  Size: "This is too long or too short.",
  Length: "This is too long or too short.",
  Pattern: "This is not in the expected format.",
  Email: "Enter a valid email address.",
  Min: "This value is too small.",
  Max: "This value is too large.",
  Positive: "Enter a number above zero.",
  PositiveOrZero: "Enter zero or more.",
  Future: "Choose a date in the future.",
  FutureOrPresent: "Choose today or a later date.",
};

// Codes that mean the frontend sent a bad request; the user can only retry or report it
const FRONTEND_BUGS = ["if-match-required", "idempotency-key-required", "invalid-idempotency-key", "idempotency-key-reused", "unknown-requirement", "invalid-marks", "unknown-criterion", "unknown-application", "invalid-status", "invalid-funding"];

export const problemMessage = (code) => {
  if (!code) return null;
  if (FRONTEND_BUGS.includes(code)) {
    console.error(`Scholarships API refused a malformed request: ${code}`);
    return "Something went wrong on our side. Please reload the page and try again.";
  }
  return MESSAGES[code] ?? FIELD_RULES[code] ?? null;
};
